import { Component, OnInit } from '@angular/core';
import { FormControl, FormGroup, FormRecord, Validators } from '@angular/forms';
import { Title } from '@angular/platform-browser';
import { ActivatedRoute, Router } from '@angular/router';
import { AdsCampaign } from 'src/app/classes/ads-campaign';
import { AdsCampaignService } from 'src/app/services/ads-campaign.service';
import AdsCampaignStatuses from 'src/app/constants/ads-campaign-statuses';
import AdsCampaignTypes from 'src/app/constants/ads-campaign-types';
import { Organization } from 'src/app/classes/organization';
import { AdsCampaignAsset } from 'src/app/classes/ads-campaign-asset';
import { AdsCampaignAssetService } from 'src/app/services/ads-campaign-asset.service';
import { AssetComponent } from './asset/asset.component';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import AdsCampaignAssetPlacements from 'src/app/constants/ads-campaign-asset-placements';
import { AdsCampaignArea } from 'src/app/classes/ads-campaign-area';
import { Shift } from 'src/app/classes/shift';
import { AreaComponent } from './area/area.component';
import { AdsCampaignAreaService } from 'src/app/services/ads-campaign-area.service';
import * as fa from '@fortawesome/free-solid-svg-icons';

type ShiftWithMatchScore = Shift & { match_score: number }

@Component({
  selector: 'app-edit',
  templateUrl: './edit.component.html',
  styleUrls: ['./edit.component.css']
})

export class EditComponent implements OnInit {
  adsCampaign: AdsCampaign = new AdsCampaign()
  organizations: Organization[] = [];

  adsCampaignStatuses = AdsCampaignStatuses;
  adsCampaignTypes = AdsCampaignTypes;
  adsCampaignAssetPlacements = AdsCampaignAssetPlacements;

  fa = fa
  activeTab: string = 'basics';

  examples: ShiftWithMatchScore[] = [];

  saving = false

  form = new FormGroup({
    name: new FormControl('', [Validators.required]),
    organization_id: new FormControl(null, [Validators.required]),
    notes: new FormControl(''),
    status: new FormControl('inactive', [Validators.required]),
    targeting_description: new FormControl(''),
    start_date: new FormControl(''),
    end_date: new FormControl(''),
    cutoff: new FormControl(0.5, [Validators.required, Validators.min(0), Validators.max(1)]),
  });

  constructor(
    private adsCampaignService: AdsCampaignService,
    private adsCampaignAssetService: AdsCampaignAssetService,
    private adsCampaignAreaService: AdsCampaignAreaService,
    private route: ActivatedRoute,
    private titleService: Title,
    private router: Router,
    private modalService: NgbModal,
  ) { }

  ngOnInit(): void {
    this.adsCampaignService.organizations().subscribe(organizations => {
      this.organizations = organizations;

      this.route.params.subscribe(params => {
        if (params['campaignId'] != 'create') {
          this.adsCampaignService.get(params['campaignId']).subscribe(adsCampaign => {
            this.adsCampaign = adsCampaign;
            this.form.patchValue(adsCampaign);

            this.titleService.setTitle(this.adsCampaign.name)
          });
        }
        else {
          this.titleService.setTitle($localize`Campaign`);
          this.adsCampaign = new AdsCampaign();
          this.form.reset();
          this.form.controls.status.setValue('inactive');
        }
      });
    });

    this.route.fragment.subscribe(fragment => {
      this.activeTab = fragment ? fragment : 'basics';
    });
  }

  save(): void {
    if (this.form.valid) {
      this.saving = true

      this.adsCampaignService.save(this.adsCampaign.id, this.form.value as AdsCampaign).subscribe({
        next: () => {
          this.saving = false
          this.router.navigate(['/campaigns']);
        },
        error: (error: any) => {
          this.saving = false
          console.error(error);
        }
      })
    }
  }

  delete(): void {
    if (confirm($localize`Are you sure?`)) {
      this.adsCampaignService.delete(this.adsCampaign.id).subscribe(() => {
        this.router.navigate(['/campaigns']);
      });
    }
  }

  setFragment(activeTab: string) {
    this.router.navigate([], { fragment: activeTab }).then(() => { })
  }

  assetEdit(asset: AdsCampaignAsset = new AdsCampaignAsset()) {
    const modal = this.modalService.open(AssetComponent, { size: 'xl', centered: true });

    asset.ads_organization = this.adsCampaign.ads_organization;

    modal.componentInstance.adsCampaign = this.adsCampaign
    modal.componentInstance.adsCampaignAsset = asset;

    modal.result.then(() => {
      this.adsCampaignService.get(this.adsCampaign.id).subscribe(adsCampaign => {
        this.adsCampaign.assets = adsCampaign.assets;
      });
    });
  }

  assetDelete(asset: AdsCampaignAsset) {
    if (confirm($localize`Are you sure?`)) {
      this.adsCampaignAssetService.delete(this.adsCampaign.id, asset.id).subscribe(() => {
        this.adsCampaign.assets = this.adsCampaign.assets.filter(a => a.id !== asset.id);
      });
    }
  }

  areaEdit(area: AdsCampaignArea = new AdsCampaignArea()) {
    const modal = this.modalService.open(AreaComponent, { size: 'xl', centered: true });

    modal.componentInstance.adsCampaign = this.adsCampaign;
    modal.componentInstance.adsCampaignArea = area;
    modal.componentInstance.organization = this.adsCampaign.organization;

    modal.result.then(() => {
      this.adsCampaignService.get(this.adsCampaign.id).subscribe(adsCampaign => {
        this.adsCampaign.areas = adsCampaign.areas;
      });
    });
  }

  areaDelete(area: AdsCampaignArea) {
    if (confirm($localize`Are you sure?`)) {
      this.adsCampaignAreaService.delete(this.adsCampaign.id, area.id).subscribe(() => {
        this.adsCampaignService.get(this.adsCampaign.id).subscribe(adsCampaign => {
          this.adsCampaign.areas = adsCampaign.areas;
        });
      })
    }
  }

  loadExamples() {
    this.adsCampaignService.examples(this.adsCampaign.id, this.form.value).subscribe(examples => {
      this.examples = examples as ShiftWithMatchScore[];
    });
  }

  clickPreview(adsCampaignAsset: AdsCampaignAsset) {
    window.open(adsCampaignAsset.url, '_blank');
  }

  copy() {
    if(confirm($localize`Are you sure?`)) {
      this.adsCampaignService.copy(this.adsCampaign.id).subscribe({
        next: (newCampaign: AdsCampaign) => {
          this.router.navigate(['/campaigns', newCampaign.id]);
        },
        error: (error: any) => {
          console.error(error);
        }
      });
    }
  }
}
